import { useCallback, useState } from "react";
import { Box, Typography } from "./Mui";
import { styled } from "@mui/material/styles";

const UpDownAnimationWrapper = styled("div")(() => ({
  [`&.up_down_animation`]: {
    animation: `up_down_animation 2500ms ease-in-out 200ms infinite`,
  },
  "@keyframes up_down_animation": {
    "0%": {
      transform: "translateY(0px)",
    },
    "50%": {
      transform: "translateY(10px)",
    },
    "100%": {
      transform: "translateY(0px)",
    },
  },
}));

enum TabValue {
  Extension = "Extension Wallet",
  App = "Poki App",
}

const Extensions = [
  {
    label: "Install for Chrome",
    url: "https://chromewebstore.google.com/detail/pokiwallet-extension/loncogjebkndondcoppimcfhengmcofl?hl=en-US&utm_source=ext_sidebar",
    value: "Chrome",
    img: "/images/chrome.svg",
  },
  {
    label: "Install for FireFox",
    value: "FireFox",
    img: "/images/firefox.svg",
  },
  {
    label: "Install for Brave",
    value: "Brave",
    img: "/images/brave.svg",
  },
];

interface TabProps {
  active: boolean;
  value: TabValue;
  onClick?: (value: TabValue) => void;
}

function Tab({ active, value, onClick }: TabProps) {
  const handleClick = useCallback(() => {
    if (onClick) {
      onClick(value);
    }
  }, [onClick, value]);

  return (
    <Box
      sx={{
        borderRadius: "50px",
        background: active ? "#28BF5B" : "transparent",
        cursor: "pointer",
      }}
      onClick={handleClick}
    >
      <Typography
        sx={{
          color: "text.primary",
          fontSize: "16px",
          fontWeight: 500,
          padding: "8px 12px",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function ExtensionWallet() {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        gap: "8px ",
        "@media(max-width: 640px)": {
          flexDirection: "column",
          alignItems: "center",
        },
      }}
    >
      {Extensions.map((extension) =>
        extension.url ? (
          <a
            key={extension.value}
            href={extension.url}
            rel="noreferrer"
            target="_blank"
            style={{ textDecoration: "none" }}
          >
            <Box
              sx={{
                background: "#171717",
                padding: "8px",
                borderRadius: "40px",
                display: "flex",
                gap: "0 8px",
                alignItems: "center",
                width: "fit-content",
              }}
            >
              <img src={extension.img} alt="" />
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "text.primary",
                  fontWeight: 500,
                }}
              >
                {extension.label}
              </Typography>
            </Box>
          </a>
        ) : (
          <Box
            key={extension.value}
            sx={{
              background: "#171717",
              padding: "8px",
              borderRadius: "40px",
              display: "flex",
              gap: "0 8px",
              alignItems: "center",
              width: "fit-content",
            }}
          >
            <img src={extension.img} alt="" />
            <Typography
              sx={{
                fontSize: "14px",
                color: "text.primary",
                fontWeight: 500,
              }}
            >
              {extension.label}
            </Typography>
          </Box>
        )
      )}
    </Box>
  );
}

function PokiApp() {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        gap: "0 8px ",
      }}
    >
      <Box
        sx={{
          background: "#181818",
          padding: "8px 12px",
          borderRadius: "8px",
        }}
      >
        <Typography
          sx={{ fontSize: "14px", color: "text.primary", fontWeight: 500 }}
        >
          Coming soon
        </Typography>
      </Box>
    </Box>
  );
}

export function Header() {
  const [activeTab, setActiveTab] = useState<TabValue>(TabValue.Extension);

  const handleTableClick = useCallback((value: TabValue) => {
    setActiveTab(value);
  }, []);

  return (
    <>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          padding: "20px 0 0 0",
        }}
      >
        <Box sx={{ maxWidth: "1040px", width: "100%" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "0 8px" }}>
            <img src="/images/logo.svg" alt="" />
            <Typography
              sx={{ color: "text.primary", fontWeight: 800, fontSize: "20px" }}
            >
              Pokiwallet
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          flexDirection: "column",
          margin: "100px 0 0 0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            maxWidth: "1040px",
            flexDirection: "column",
            position: "relative",
            width: "100%",
          }}
        >
          <Box sx={{ position: "absolute", zIndex: -1 }}>
            <img src="/images/header-background.svg" alt="" />
          </Box>

          <UpDownAnimationWrapper
            className="up_down_animation"
            sx={{
              position: "absolute",
              right: "-10px",
              top: "-10px",
              "@media(max-width:640px)": {
                display: "none",
              },
            }}
          >
            <img src="/images/header-usd.svg" alt="" />
          </UpDownAnimationWrapper>

          <UpDownAnimationWrapper
            className="up_down_animation"
            sx={{
              position: "absolute",
              right: "0px",
              bottom: "10px",
              "@media(max-width:640px)": {
                display: "none",
              },
            }}
          >
            <img src="/images/header-receive.svg" alt="" />
          </UpDownAnimationWrapper>

          <UpDownAnimationWrapper
            className="up_down_animation"
            sx={{
              position: "absolute",
              left: "0px",
              bottom: "80px",
              "@media(max-width:640px)": {
                display: "none",
              },
            }}
          >
            <img src="/images/header-icp.svg" alt="" />
          </UpDownAnimationWrapper>

          <Typography
            sx={{
              fontSize: "64px",
              color: "text.primary",
              fontWeight: 500,
              "@media(max-width: 640px)": {
                fontSize: "30px",
              },
            }}
          >
            Poki Wallet
          </Typography>
          <Typography
            sx={{
              fontSize: "64px",
              color: "text.primary",
              "@media(max-width: 640px)": {
                fontSize: "30px",
              },
            }}
          >
            <Typography
              sx={{
                fontSize: "64px",
                color: "text.primary",
                fontWeight: 500,
                "@media(max-width: 640px)": {
                  fontSize: "30px",
                },
              }}
              component="span"
            >
              <Typography
                sx={{
                  fontSize: "64px",
                  color: "#28BF5B",
                  fontWeight: 500,
                  "@media(max-width: 640px)": {
                    fontSize: "30px",
                  },
                }}
                component="span"
              >
                Own
              </Typography>
              &nbsp;It,
            </Typography>
            <Typography
              sx={{
                fontSize: "64px",
                color: "text.primary",
                fontWeight: 500,
                "@media(max-width: 640px)": {
                  fontSize: "30px",
                },
              }}
              component="span"
            >
              &nbsp;
              <Typography
                sx={{
                  fontSize: "64px",
                  color: "#28BF5B",
                  fontWeight: 500,
                  "@media(max-width: 640px)": {
                    fontSize: "30px",
                  },
                }}
                component="span"
              >
                Share
              </Typography>
              &nbsp;It,
            </Typography>
            <Typography
              sx={{
                fontSize: "64px",
                color: "text.primary",
                fontWeight: 500,
                "@media(max-width: 640px)": {
                  fontSize: "30px",
                },
              }}
              component="span"
            >
              &nbsp;
              <Typography
                sx={{
                  fontSize: "64px",
                  color: "#28BF5B",
                  fontWeight: 500,
                  "@media(max-width: 640px)": {
                    fontSize: "30px",
                  },
                }}
                component="span"
              >
                Lead
              </Typography>
              &nbsp;It
            </Typography>
          </Typography>

          <Typography
            sx={{
              color: "text.primary",
              fontSize: "16px",
              fontWeight: 400,
              width: "100%",
              maxWidth: "526px",
              textAlign: "center",
              "@media(max-width: 640px)": {
                fontSize: "14px",
              },
            }}
          >
            Poki Wallet is a decentralized wallet in the ICP ecosystem that
            integrates file storage and token-sharing. It lets users manage
            assets, store files, and share tokens or files anytime, anywhere,
            free from centralized platform restrictions, enabling true Web3
            freedom!
          </Typography>

          <Box sx={{ margin: "60px 0 0 0" }}>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Box
                sx={{
                  border: "1px solid #28BF5B",
                  padding: "4px",
                  borderRadius: "50px",
                  display: "flex",
                  gap: "8px 4px",
                  flexWrap: "wrap",
                }}
              >
                {[TabValue.Extension, TabValue.App].map((value) => (
                  <Tab
                    key={value}
                    active={activeTab === value}
                    onClick={handleTableClick}
                    value={value}
                  />
                ))}
              </Box>
            </Box>

            <Box sx={{ margin: "28px 0 0 0" }}>
              {activeTab === TabValue.Extension ? <ExtensionWallet /> : null}
              {activeTab === TabValue.App ? <PokiApp /> : null}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}
